<script setup lang="ts">
import { computed, ref } from 'vue';
import { useMutation } from '@tanstack/vue-query';
import {
  patientServiceCategoriesApi,
  type PatientServiceCategory,
} from '@/api/patient-service-categories';

interface Props {
  patientServiceCategory: PatientServiceCategory;
}

interface Emits {
  (e: 'close'): void;
  (e: 'success'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const submitButtonDisabled = ref(false);

const keptSubcategoriesCount = computed(() =>
  props.patientServiceCategory.patientServiceSubcategories
    .filter((subcategory) => !subcategory.discarded).length);

const discardMutation = useMutation({
  mutationFn: () => patientServiceCategoriesApi.destroy(props.patientServiceCategory.id),
  onSuccess: () => {
    emit('success');
    emit('close');
  },
  onError: () => {
    submitButtonDisabled.value = false;
  },
});

const errorMessage = computed(() => {
  const error = discardMutation.error.value as
    { response?: { data?: { errors?: string } } } | null;
  if (!error) return null;

  return error.response?.data?.errors || 'Ha ocurrido un error. Intenta nuevamente.';
});

function handleSubmit() {
  submitButtonDisabled.value = true;
  discardMutation.mutate();
}
</script>
<template>
  <v-card class="pa-4">
    <v-card-title>
      Archivar Categoría "{{ patientServiceCategory.name }}"
    </v-card-title>
    <v-card-text>
      <p class="mb-4">
        ¿Está seguro que desea archivar la categoría "{{ patientServiceCategory.name }}"?
      </p>
      <p class="mb-4 text-body-2 text-medium-emphasis">
        La categoría será archivada junto con sus subcategorías y dejará de estar disponible
        para asignar pacientes. Esta acción se puede revertir más tarde si es necesario.
      </p>
      <v-alert
        v-if="keptSubcategoriesCount > 0"
        type="warning"
        variant="tonal"
        class="mb-4"
      >
        Esta categoría tiene {{ keptSubcategoriesCount }} subcategorías que también
        serán archivadas.
      </v-alert>
      <v-alert
        type="info"
        variant="tonal"
        class="mb-4"
      >
        No se puede archivar si hay pacientes asignados actualmente o a futuro:
        primero cambia su categoría.
      </v-alert>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        :disabled="discardMutation.isPending.value"
        @click="emit('close')"
      >
        Cancelar
      </v-btn>
      <v-btn
        color="warning"
        :loading="discardMutation.isPending.value"
        :disabled="submitButtonDisabled"
        @click="handleSubmit"
      >
        Archivar
      </v-btn>
    </v-card-actions>

    <v-alert
      v-if="errorMessage"
      type="error"
      variant="tonal"
      class="mt-4"
    >
      {{ errorMessage }}
    </v-alert>
  </v-card>
</template>
